// Reemplaza a CourierAppConfigPage.jsx (borrada junto con el resto del portal de cadetes
// freelance), que era la única pantalla desde donde se podía publicar una nueva versión de
// Términos y Condiciones / Política de Privacidad. Ese documento NO es específico de cadetes:
// lo muestra públicamente axium-landing en /terminos y /privacidad. Ver LegalDocumentService.js
// y, en axium-backend, com.axium.legal.LegalController.
import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Alert,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LegalDocumentService from '../../../services/LegalDocumentService';

const LegalDocumentsPage = () => {
  const [termsText, setTermsText] = useState('');
  const [privacyText, setPrivacyText] = useState('');
  const [dataDeletionText, setDataDeletionText] = useState('');
  const [currentVersion, setCurrentVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [current, versionList] = await Promise.all([
        LegalDocumentService.getCurrent(),
        LegalDocumentService.getVersions()
      ]);
      setCurrentVersion(current?.hasActiveVersion ? current.version : null);
      setTermsText(current?.termsText || '');
      setPrivacyText(current?.privacyText || '');
      setDataDeletionText(current?.dataDeletionText || '');
      setVersions(versionList);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handlePublish = async () => {
    setErrorMessage('');
    if (!termsText.trim() || !privacyText.trim() || !dataDeletionText.trim()) {
      setErrorMessage('Términos, Política de Privacidad e Instrucciones de Eliminación de Datos son obligatorios.');
      return;
    }
    setPublishing(true);
    try {
      await LegalDocumentService.publish(termsText, privacyText, dataDeletionText);
      setSuccessOpen(true);
      await loadAll();
    } catch (e) {
      setErrorMessage(e?.response?.data?.error || 'No se pudo publicar la nueva versión.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Términos, Privacidad y Eliminación de Datos"
        subtitle="Documento público mostrado en axium-landing (/terminos, /privacidad y /eliminar-datos). Publicar una nueva versión reemplaza la vigente para todos los visitantes del sitio."
      />

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Versión vigente"
          subheader={
            currentVersion
              ? `Versión ${currentVersion} activa`
              : 'No hay ninguna versión publicada todavía'
          }
        />
        <CardContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={14}
                label="Términos y Condiciones"
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                disabled={loading}
                helperText="Se admite HTML simple (h2, h3, p, ul, li, strong): así es como lo renderiza la landing."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={14}
                label="Política de Privacidad"
                value={privacyText}
                onChange={(e) => setPrivacyText(e.target.value)}
                disabled={loading}
                helperText="Se admite HTML simple (h2, h3, p, ul, li, strong): así es como lo renderiza la landing."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={14}
                label="Eliminar datos"
                value={dataDeletionText}
                onChange={(e) => setDataDeletionText(e.target.value)}
                disabled={loading}
                helperText="Instrucciones para que un usuario pida eliminar sus datos (requerido por Meta para las integraciones de Instagram/Facebook). Se admite HTML simple (h2, h3, p, ul, li, strong)."
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <LoadingButton variant="contained" onClick={handlePublish} loading={publishing} disabled={loading}>
              Publicar nueva versión
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardHeader title="Historial de versiones" />
        <CardContent>
          {versions.length === 0 ? (
            <Alert severity="info">Todavía no hay versiones publicadas.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Versión</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Publicado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.version}</TableCell>
                    <TableCell>
                      {v.isActive ? <Chip size="small" color="success" label="Vigente" /> : 'Reemplazada'}
                    </TableCell>
                    <TableCell>{v.createdAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Snackbar open={successOpen} autoHideDuration={2500} onClose={() => setSuccessOpen(false)}>
        <Alert severity="success" onClose={() => setSuccessOpen(false)}>
          Nueva versión publicada
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LegalDocumentsPage;
